import { TreatmentWithAssignedGroup } from '../../services/experiments/experiments'
import { getConfig } from '../../../config'
import { Placement } from '../../../contexts/placement-context'
import { DesktopOsType, MobileOsType } from '../../services/facade'
import { User } from '../../services/user'
import { encodeSha256 } from '../../utils/crypto'
import { GA4Event, Template, GtmEvent, LlamaProductName, DataLayer, eventToTemplateName } from './types'
import { pageSlugToExpToTrackMap } from './constants'
import { logDataLayerPushError } from './utils'
import { Lang } from '@lib/utils/locale'
import { DEFAULT_LANG } from '@lib/constants'

const layer = getConfig().acquisitionTracking.googleTagManager.layer

const gtmPush = (data: object): void => {
  if (IS_CLIENT_SIDE) {
    // #ToDo Improve typings to avoid any usage
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let gtmTracker = (window as any)[layer]
    if (!gtmTracker) {
      // @ts-expect-error It's okay to make data layer an array, GTM will fix it up when it loads
      window[layer] = []
      // #ToDo Improve typings to avoid any usage
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gtmTracker = (window as any)[layer]
    }
    gtmTracker.push(data)
  }
}

const gtmPushEvent = (event: GtmEvent, props?: DataLayer): void => {
  gtmPush({ event, ...props })
}

const gtmPushGA4Event = (event: GtmEvent, template: Template, props?: DataLayer): void => {
  gtmPush({ event, template_name: template, ...props })
}

export const trackExtensionInstall = (placement: Placement | undefined) => {
  gtmPushEvent('extensionInstalled')
  trackGA4BtnClick(placement)
}

export const trackLlamaInstall = (platformName: DesktopOsType, placement: Placement | undefined) => {
  gtmPushEvent(`${platformName}LlamaDesktopDownload`)
  trackGA4DownloadBtnClick(
    platformName === DesktopOsType.Windows ? LlamaProductName.LlamaWindows : LlamaProductName.LlamaMac,
    placement
  )
}

export const trackKeyboardInstall = (platformName: MobileOsType, placement: Placement | undefined) => {
  gtmPushEvent(`${platformName}KeyboardMobileDownload`)
  trackGA4BtnClick(placement)
}

export const trackAccountCreation = () => {
  gtmPushEvent('accountCreation')
}

export const trackEncodedUserId = (encodedUserId: string) => {
  gtmPush({ encodedUserId })
}

export const trackGA4PageView = (user: User) => {
  const {
    id,
    email,
    isAnonymous,
    type: subscription,
    customFieldRole: role,
    industryType: industry,
    teamType: team,
    companySize,
    graduationYear,
  } = user || {}
  const nonEncodedData = { role, industry, team, companySize, graduationYear, subscription }
  const event = GA4Event.pageView

  if (isAnonymous) {
    gtmPushGA4Event(event, eventToTemplateName[GA4Event.pageView], {
      // TODO: Remove user_id once the variable for user.id is added to GTM. We want to keep both user_id and user.id till then.
      user_id: null,
      user: {
        id: undefined,
        email: undefined,
        ...nonEncodedData,
      },
    })
  } else {
    Promise.all([encodeSha256(id), encodeSha256(email)])
      .then(([encodedUserId, encodedUserEmail]) => {
        gtmPushGA4Event(event, eventToTemplateName[GA4Event.pageView], {
          user_id: encodedUserId,
          user: {
            id: encodedUserId,
            email: encodedUserEmail,
            ...nonEncodedData,
          },
        })
      })
      .catch((e) => logDataLayerPushError(e, 'user_id'))
  }
}

export const trackGA4BtnClick = (placement: Placement | undefined, name?: string) => {
  gtmPushGA4Event(GA4Event.btnClick, eventToTemplateName[GA4Event.btnClick], {
    btn_name: name,
    btn_location: placement,
  })
}

export const trackGA4DownloadBtnClick = (
  productName: LlamaProductName,
  placement: Placement | undefined,
  name?: string
) => {
  gtmPushGA4Event(GA4Event.btnClickDownload, eventToTemplateName[GA4Event.btnClickDownload], {
    btn_name: name,
    btn_location: placement,
    product_name: productName,
  })
}

export const trackContentInfo = (browserName: string, lang: Lang = DEFAULT_LANG) => {
  gtmPush({ content: { browserName, lang } })
}

export const trackExperimentInfo = (pageSlug: string, assignedTreatments: Map<string, TreatmentWithAssignedGroup>) => {
  if (typeof window !== 'undefined') {
    const expToTrackOnPage = pageSlugToExpToTrackMap.get(pageSlug)
    const { experimentId, experimentName, assignedGroup } =
      (expToTrackOnPage && assignedTreatments.get(expToTrackOnPage)) || {}
    // Event if we have no treatments to track for the page, we still want to add experiment info as undefined
    gtmPush({ experiment: { id: experimentId, name: experimentName, variant: assignedGroup } })
  }
}
